import { jwtDecode } from 'jwt-decode';

export interface DecodedToken {
  userId: string;
  tenantId: string;
  email: string;
  roles: string[];
  displayName: string;
  picture?: string;
  id: string;
  exp?: number;
  iat?: number;
}

/**
 * Safely decode a JWT token
 * @param token - The JWT token to decode
 * @returns Decoded token or null if invalid
 */
export const safeDecodeToken = (token: string | null): DecodedToken | null => {
  if (!token) {
    return null;
  }

  try {
    // Check if token has the correct format (3 parts separated by dots)
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('Invalid token format: token must have 3 parts');
      return null;
    }

    const decoded = jwtDecode<DecodedToken>(token);
    return decoded;
  } catch (error) {
    console.error('Failed to decode token:', error);
    return null;
  }
};

/**
 * Check if a token is expired
 * @param token - The JWT token to check
 * @returns true if expired, false if valid
 */
export const isTokenExpired = (token: string | null): boolean => {
  const decoded = safeDecodeToken(token);
  
  if (!decoded || !decoded.exp) {
    return true;
  }

  // exp is in seconds, Date.now() is in milliseconds
  const currentTime = Math.floor(Date.now() / 1000);
  return decoded.exp < currentTime;
};

/**
 * Check if a token will expire soon (within specified minutes)
 * @param token - The JWT token to check
 * @param minutesThreshold - Minutes before expiration to consider "expiring soon" (default: 5)
 * @returns true if token expires within threshold, false otherwise
 */
export const isTokenExpiringSoon = (token: string | null, minutesThreshold: number = 5): boolean => {
  const decoded = safeDecodeToken(token);
  
  if (!decoded || !decoded.exp) {
    return true;
  }

  const currentTime = Math.floor(Date.now() / 1000);
  const thresholdTime = currentTime + (minutesThreshold * 60);
  
  return decoded.exp < thresholdTime;
};

/**
 * Get the expiration time of a token
 * @param token - The JWT token
 * @returns Expiration date or null if invalid
 */
export const getTokenExpiration = (token: string | null): Date | null => {
  const decoded = safeDecodeToken(token);
  
  if (!decoded || !decoded.exp) {
    return null;
  }

  return new Date(decoded.exp * 1000);
};

/**
 * Get time remaining until token expiration
 * @param token - The JWT token
 * @returns Milliseconds until expiration, or 0 if expired/invalid
 */
export const getTimeUntilExpiration = (token: string | null): number => {
  const decoded = safeDecodeToken(token);
  
  if (!decoded || !decoded.exp) {
    return 0;
  }

  const currentTime = Math.floor(Date.now() / 1000);
  const timeRemaining = decoded.exp - currentTime;
  
  return Math.max(0, timeRemaining * 1000); // Convert to milliseconds
};

/**
 * Validate token structure and expiration
 * @param token - The JWT token to validate
 * @returns true if valid and not expired, false otherwise
 */
export const isTokenValid = (token: string | null): boolean => {
  if (!token) {
    return false;
  }

  const decoded = safeDecodeToken(token);
  if (!decoded) {
    return false;
  }

  // Check if token is expired
  if (isTokenExpired(token)) {
    return false;
  }

  // Additional validation: check required fields
  if (!decoded.userId || !decoded.email || !decoded.roles) {
    console.error('Token missing required fields');
    return false;
  }

  return true;
};

/**
 * Clear tokens and redirect to login
 * Use this when tokens are invalid or expired
 */
export const clearTokensAndRedirect = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('userRole');
  localStorage.removeItem('displayName');
  
  // Only redirect if not already on login page
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};
